import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Grid, Chip, CircularProgress,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions, IconButton
} from '@mui/material';
import { Add, Delete, ArrowForward, Edit } from '@mui/icons-material';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const AllProjects = () => {
  const navigate = useNavigate();
  const { user, setSelectedProjectId } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(null);

  const fetchProjects = async () => {
    try {
      const res = await api.get('/projects');
      setProjects(res.data);
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProjects(); }, []);

  const handleOpen = (project) => {
    setSelectedProjectId(project.id);
    navigate('/');
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      await api.delete(`/projects/${deleteDialog.id}`);
      toast.success('Project deleted');
      fetchProjects();
    } catch (err) {
      toast.error('Failed to delete project');
    } finally {
      setDeleteDialog(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600} color="#2c3e50">
          📁 All Projects
        </Typography>
        {user && (
        <Button
          onClick={() => navigate('/projects/new')}
          variant="contained"
          startIcon={<Add />}
          sx={{ bgcolor: '#1abc9c', '&:hover': { bgcolor: '#16a085' } }}
        >
          New Project
        </Button>
        )}
      </Box>

      {projects.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">
            No projects yet. Create your first project to get started.
          </Typography>
          {user && (
          <Button
            onClick={() => navigate('/projects/new')}
            variant="contained"
            startIcon={<Add />}
            sx={{ mt: 2, bgcolor: '#1abc9c' }}
          >
            Create Project
          </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {projects.map(project => (
            <Grid item xs={12} key={project.id}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: '10px',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: '#1abc9c', boxShadow: '0 2px 8px rgba(26,188,156,0.15)' }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={600} sx={{ color: '#2c3e50', mb: 0.5 }}>
                      📁 {project.name}
                    </Typography>
                    {project.description && (
                      <Typography variant="body2" sx={{ color: '#7f8c8d', mb: 1 }}>
                        {project.description}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Chip
                        label={`${project.analyses_count} analyses`}
                        size="small"
                        sx={{ bgcolor: '#e8f8f5', color: '#1abc9c', fontWeight: 500 }}
                      />
                      <Chip
                        label={`${project.datasets_count} datasets`}
                        size="small"
                        sx={{ bgcolor: '#eaf2f8', color: '#2980b9', fontWeight: 500 }}
                      />
                      {project.manager && (
                        <Chip
                          label={`Manager: ${project.manager.email}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {project.validator_ref && (
                        <Chip
                          label={`Validator: ${project.validator_ref.email}`}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: '#27ae60', color: '#27ae60' }}
                        />
                      )}
                      <Typography variant="caption" color="textSecondary">
                        Created: {new Date(project.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button onClick={() => handleOpen(project)} size="small" variant="contained" endIcon={<ArrowForward />} sx={{ bgcolor: '#1abc9c', '&:hover': { bgcolor: '#16a085' } }}>Open</Button>
                    <Button onClick={() => navigate(`/projects/${project.id}/edit`)} size="small" variant="outlined" startIcon={<Edit />}>Edit</Button>
                    <IconButton
                      onClick={() => setDeleteDialog(project)}
                      size="small"
                      color="error"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>"{deleteDialog?.name}"</strong>?
            This will permanently delete all associated analyses and datasets.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllProjects;
